<!--
  Licenses.svelte — the signed-in user's licences, per product.

  AbstractID reads these live from each product's licensing backend and stores
  none of them, so this page is a view onto InstaLOD's and InstaMAT's own records
  rather than a second copy of them. Its reason for existing is that a user
  holding licences for both products otherwise has to sign into two applications
  to answer one question: what do I have, and where is it running?

  Administration of other people's licences stays in the licensing application.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Badge,
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Toggle
	} from 'flowbite-svelte';
	import {
		ApplicationLinks,
		EmptyState,
		LicenseAppIcon,
		LicenseDownloads,
		Panel,
		RoutedLink,
		SeatMeter
	} from '../../components';
	import { listLicenseApplications, listLicenses } from '../../AppNetwork';
	import type { License, LicenseApplicationOverview } from '../../AppTypes';
	import { location, push } from '../../abstract-spa-router';
	import { t, t2 } from '../../i18n';

	/**
	 * Which panel each section route opens, keyed by its first path segment.
	 *
	 * The three routes render this one component — the path is the only thing
	 * that differs between them, so it is the only thing the page reads to decide
	 * what it is showing.
	 */
	const SECTIONS: Record<string, 'licenses' | 'downloads' | 'documentation'> = {
		'/Licenses': 'licenses',
		'/Downloads': 'downloads',
		'/Documentation': 'documentation'
	};

	interface Props {
		/** Route params — `appUUID` selects which product's licences are shown. */
		params?: { appUUID?: string };
	}

	let { params = {} }: Props = $props();

	/** The licensing applications the account can see. */
	let apps: LicenseApplicationOverview[] = $state([]);
	/** Licences of the selected application. */
	let licenses: License[] = $state([]);
	/** Which application is selected. */
	let selected = $state('');
	/** Whether the application list is still loading. */
	let loadingApps = $state(true);
	/** Whether the licence list is still loading. */
	let loadingLicenses = $state(false);
	/** Failure of the application list. */
	let appsError = $state('');
	/** Failure of the licence list, including an unreachable backend. */
	let licensesError = $state('');

	/** Load the licensing applications and settle on one to show. */
	async function loadApps(): Promise<void> {
		loadingApps = true;
		appsError = '';
		/** Fetch the applications and pick the one named in the route, else the first. */
		try {
			apps = await listLicenseApplications();
			const requested = apps.find((app) => app.applicationUUID === params.appUUID);
			selected = requested?.applicationUUID ?? apps[0]?.applicationUUID ?? '';
			if (selected) await loadLicenses(selected);
		} catch (e) {
			appsError = e instanceof Error ? e.message : t2('Failed to load licenses');
		} finally {
			loadingApps = false;
		}
	}

	/** Load one application's licences. */
	async function loadLicenses(appUUID: string): Promise<void> {
		loadingLicenses = true;
		licensesError = '';
		/** Fetch the page of licences for the selected application. */
		try {
			const page = await listLicenses(appUUID, { limit: 100 });
			/* Guard the shape rather than trusting it: a malformed page would otherwise
			   throw inside the template, taking the whole view down instead of
			   degrading to an empty list. */
			licenses = Array.isArray(page.items) ? page.items : [];
		} catch (e) {
			licenses = [];
			licensesError = e instanceof Error ? e.message : t2('Failed to load licenses');
		} finally {
			loadingLicenses = false;
		}
	}

	/** Whether lapsed licences are listed alongside the live ones. */
	let showExpired = $state(false);

	/**
	 * Whether a licence has lapsed.
	 *
	 * Deliberately **not** `!isActive`: that is "in date and not paused", so it also
	 * catches a paused licence — which is suspended, not expired, and would vanish
	 * under a toggle that never claimed to hide it. This is the same rule the status
	 * badge renders, derived once so the two cannot drift apart.
	 */
	function isExpired(license: License): boolean {
		return !license.isActive && !license.isPaused;
	}

	/** Licences currently listed. */
	let visibleLicenses = $derived(showExpired ? licenses : licenses.filter((license) => !isExpired(license)));

	/** How many licences the toggle is holding back. */
	let expiredCount = $derived(licenses.filter((license) => isExpired(license)).length);

	/** The application currently selected, for its links and display name. */
	let selectedApp = $derived(apps.find((app) => app.applicationUUID === selected) ?? null);

	/** Whether the selected product publishes documentation links. */
	let hasDocumentation = $derived((selectedApp?.documentationLinks.length ?? 0) > 0);

	/** The route the page was reached by, which is what names its section. */
	let sectionPath = $derived(`/${$location.split('/')[1] ?? ''}`);

	/** The panel this route opens. An unknown path can only be the licence list. */
	let section = $derived(SECTIONS[sectionPath] ?? 'licenses');

	/**
	 * The page's heading, named for the section on screen.
	 *
	 * One page under three names: a reader who asked for Downloads should not be
	 * told they are looking at their licences.
	 */
	let headingTitle = $derived(
		section === 'downloads' ? $t('Downloads') : section === 'documentation' ? $t('Documentation') : $t('My Licenses')
	);

	/** The standfirst under {@link headingTitle}. */
	let headingDescription = $derived(
		section === 'downloads'
			? $t('Builds available to you for each Abstract product you are licensed for.')
			: section === 'documentation'
				? $t('Guides and references for each Abstract product.')
				: $t('Licenses issued to you across Abstract products, and the machines using them.')
	);

	/** Collapse state of the downloads panel. */
	let downloadsHidden = $state(false);
	/** Collapse state of the licence panel. */
	let licensesHidden = $state(false);
	/** Collapse state of the documentation panel. */
	let documentationHidden = $state(false);

	/**
	 * Opens this route's panel and collapses the other two.
	 *
	 * The three routes are one page, so what separates them is which panel is
	 * open: a row that only scrolled would leave the reader looking at the
	 * section they asked for with two others open above and below it. Collapsing
	 * the rest is what makes three routes read as three destinations while
	 * remaining one page — every panel is still one click away, and a reader who
	 * opens a second one keeps both, since nothing closes a panel again until the
	 * route changes.
	 *
	 * Writes only — reading any of these back here would make the effect depend
	 * on what it just wrote and re-trigger itself until Svelte gives up.
	 */
	$effect(() => {
		const target = section;
		downloadsHidden = target !== 'downloads';
		licensesHidden = target !== 'licenses';
		documentationHidden = target !== 'documentation';
	});

	/**
	 * Switch products, keeping the URL in step so the choice survives a reload.
	 *
	 * The section stays put: a reader who came in through Downloads and then
	 * switched products came to compare downloads, not to be moved to another
	 * page. An unknown path resolves to the licence route, so the URL that comes
	 * back is always one of the three.
	 */
	async function selectApp(appUUID: string): Promise<void> {
		if (appUUID === selected) return;
		selected = appUUID;
		push(`${SECTIONS[sectionPath] ? sectionPath : '/Licenses'}/${appUUID}`);
		await loadLicenses(appUUID);
	}

	onMount(loadApps);

	/** Formats a date, or a dash when there is none. */
	function formatDate(date: string | null): string {
		if (!date) return '—';
		return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
	}
</script>

<div class="space-y-6">
	<div>
		<h3 class="text-xl font-semibold text-gray-900 dark:text-white">{headingTitle}</h3>
		<p class="mt-1 text-sm text-gray-500 dark:text-gray-400">{headingDescription}</p>
	</div>

	{#if loadingApps}
		<EmptyState variant="loading" />
	{:else if appsError}
		<EmptyState variant="error" description={appsError} />
	{:else if apps.length === 0}
		<EmptyState
			faIcon="key"
			title={$t('No licensed products')}
			description={$t('None of the products you have access to manage licenses through Abstract ID.')}
		/>
	{:else}
		<!-- Product picker. Shown even for a single product, so the page always says
		     which records are on screen rather than leaving it implied. -->
		<!-- `!p-1` evens the frame out. `.controlpanel` is `px-3 py-1`, which suits the
		     small filter controls it was built for; these are full pills, so the
		     wider sides read as the pill sitting off-centre in its own container.
		     The `!` is the repo's idiom for beating a component class's own padding
		     (see the tables' `!p-1`), rather than relying on layer order. -->
		<div class="controlpanel flex flex-wrap gap-2 !p-1">
			{#each apps as app (app.applicationUUID)}
				<button
					type="button"
					onclick={() => selectApp(app.applicationUUID)}
					class="flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition {app.applicationUUID ===
					selected
						? 'bg-primary-100 text-primary-600 dark:bg-primary-600/20 dark:text-primary-400'
						: 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'}"
				>
					<LicenseAppIcon iconImageName={app.iconImageName} icon={app.icon} name={app.name} class="h-4 w-4" />
					{app.name}
					{#if app.summary}
						<span class="text-xs opacity-70">{app.summary.licenseCount}</span>
					{/if}
				</button>
			{/each}
		</div>

		<!-- Downloads first: a licence is the reason you may have the build, but the
		     build is what people came for. The anchor sits on a wrapper because
		     `Panel` does not forward arbitrary attributes. -->
		<div id="downloads" class="scroll-mt-6">
			<Panel title={$t('Downloads')} faIcon="download" bind:isHidden={downloadsHidden}>
				<LicenseDownloads appUUID={selected} groups={selectedApp?.downloadGroups ?? []} />
			</Panel>
		</div>

		<div id="licenses" class="scroll-mt-6">
			<Panel title={$t('Licenses')} faIcon="key" bind:isHidden={licensesHidden}>
				<!-- Offered only when it would do something. A switch that visibly
				     changes nothing is worse than no switch. Panel ignores clicks on
				     inputs and labels in its title bar, so this does not collapse it. -->
				{#snippet titleRight()}
					{#if expiredCount > 0}
						<Toggle bind:checked={showExpired} class="text-xs">
							{$t('Show {{count}} expired', { count: expiredCount })}
						</Toggle>
					{/if}
				{/snippet}

				<Table divClass="widget !p-1 relative" class="dataTable">
					<TableHead>
						<TableHeadCell>{$t('License')}</TableHeadCell>
						<TableHeadCell class="w-32">{$t('Status')}</TableHeadCell>
						<TableHeadCell class="w-44">{$t('Seats')}</TableHeadCell>
						<TableHeadCell>{$t('Entitlements')}</TableHeadCell>
						<TableHeadCell class="w-32">{$t('Expires')}</TableHeadCell>
					</TableHead>
					<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
						{#if loadingLicenses}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell colspan={5}><EmptyState variant="loading" compact /></TableBodyCell>
							</TableBodyRow>
						{:else if licensesError}
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell colspan={5}>
									<EmptyState variant="error" description={licensesError} compact />
								</TableBodyCell>
							</TableBodyRow>
						{:else if visibleLicenses.length === 0}
							<!-- Keyed on what is *shown*, not what was loaded: with everything
							     expired and hidden the body would otherwise render empty with
							     nothing to explain it, which reads as a broken table. -->
							<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
								<TableBodyCell colspan={5}>
									<EmptyState
										faIcon="key"
										title={expiredCount > 0 ? $t('No active licenses') : $t('No licenses')}
										description={expiredCount > 0
											? $t('All {{count}} of your licenses for this product have expired.', {
													count: expiredCount
												})
											: $t('You have no licenses for this product yet.')}
										compact
									>
										<!-- Only offered when the product says where to go. Inventing a
								     destination would be worse than making no offer. -->
										{#if selectedApp?.licensePurchaseURL}
											<a
												href={selectedApp.licensePurchaseURL}
												target="_blank"
												rel="noopener noreferrer"
												class="bg-primary-600 hover:bg-primary-700 mt-3 inline-flex items-center rounded-lg px-4 py-2 text-sm font-medium text-white transition"
											>
												<i class="fa-solid fa-cart-shopping me-2"></i>{$t(
													'Get a license for {{name}}',
													{
														name: selectedApp.name
													}
												)}
											</a>
										{/if}
									</EmptyState>
								</TableBodyCell>
							</TableBodyRow>
						{:else}
							{#each visibleLicenses as license (license.licenseUUID)}
								<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
									<TableBodyCell>
										<RoutedLink to="/Licenses/{selected}/{license.licenseUUID}">
											<span class="font-medium">{license.projectName || license.licenseUUID}</span
											>
										</RoutedLink>
										{#if license.isSublicense}
											<span class="ms-2 text-xs text-gray-400 dark:text-gray-500"
												>{$t('Assigned seat')}</span
											>
										{/if}
									</TableBodyCell>
									<TableBodyCell>
										{#if license.isPaused}
											<Badge color="yellow">{$t('Paused')}</Badge>
										{:else if license.isActive}
											<Badge color="green">{$t('Active')}</Badge>
										{:else}
											<Badge color="red">{$t('Expired')}</Badge>
										{/if}
									</TableBodyCell>
									<TableBodyCell>
										<SeatMeter used={license.seats.used} total={license.seats.total} />
									</TableBodyCell>
									<TableBodyCell>
										{#if license.entitlements.length === 0}
											<span class="text-xs text-gray-400 dark:text-gray-500">—</span>
										{:else}
											<div class="flex flex-wrap gap-1">
												{#each license.entitlements as entitlement (entitlement.entitlementUUID ?? entitlement.name)}
													<!-- `title` rather than a popover: these are short clarifications
											     on a badge in a table row, and a hover card per badge would
											     put a dozen of them on one screen. The cursor hint is what
											     says a description is there to read. -->
													<Badge
														color="blue"
														title={entitlement.description ?? undefined}
														class={entitlement.description ? 'cursor-help' : ''}
													>
														{entitlement.name}
													</Badge>
												{/each}
											</div>
										{/if}
									</TableBodyCell>
									<TableBodyCell class="!whitespace-nowrap"
										>{formatDate(license.endDate)}</TableBodyCell
									>
								</TableBodyRow>
							{/each}
						{/if}
					</TableBody>
				</Table>
			</Panel>
		</div>

		<!-- Documentation last: it is reference material, reached after the thing
		     you came to check. Always present, even when the product publishes
		     none — it is a route of its own, and a destination that renders
		     nothing reads as a broken page rather than as an empty shelf. -->
		<div id="documentation" class="scroll-mt-6">
			<Panel title={$t('Documentation')} faIcon="book" bind:isHidden={documentationHidden}>
				{#if selectedApp && hasDocumentation}
					<ApplicationLinks links={selectedApp.documentationLinks} />
				{:else}
					<EmptyState
						faIcon="book"
						title={$t('No documentation')}
						description={$t('This product publishes no documentation yet.')}
						compact
					/>
				{/if}
			</Panel>
		</div>
	{/if}
</div>
